import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Alert,
  Box, Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import config from '../../../../../../../../../../config';
import { SessionSummaryFieldsFragment } from '../../../../../../../../../../generated/core-api/graphql-react-query';
import useEnableSummary from './Hooks/useEnableSummary';
import useSessionSummary from './Hooks/useSessionSummary';
import StyledBox from './styles';

interface SessionResumeProps {
  session: SessionSummaryFieldsFragment;
}

interface SummaryContentProps {
  session: SessionSummaryFieldsFragment;
  generatedSummary: string;
  loading: boolean;
  generateSummary: () => void;
}

export function SummaryContent(props: SummaryContentProps) {
  const {
    session, generatedSummary, loading, generateSummary,
  } = props;
  const { t } = useTranslation('session');

  if (session.summary) {
    return <ReactMarkdown className="markdown">{session.summary}</ReactMarkdown>;
  }
  if (loading) {
    return <CircularProgress />;
  }

  if (generatedSummary) {
    return <ReactMarkdown className="markdown">{generatedSummary}</ReactMarkdown>;
  }

  return (
    <Box className="cta">
      <Typography title={session.duration < config.Live.Audio.MinimumSessionDuration ? t('details.summary.minimumDuration', { duration: config.Live.Audio.MinimumSessionDuration / 1000 })
        : t('details.summary.generateSummary')}
      >
        {t('details.summary.empty')}
      </Typography>
      <Button
        variant="outlined"
        color="primary"
        onClick={() => { generateSummary(); }}
        disabled={loading || (session.duration < config.Live.Audio.MinimumSessionDuration)}
      >
        {t('details.summary.generateSummary')}
      </Button>
    </Box>
  );
}

export default function SessionSummary(props: SessionResumeProps) {
  const { session } = props;
  const { t } = useTranslation('session');
  const {
    generateSummary, text, loading, error,
  } = useSessionSummary(session.id);

  const enableSummary = useEnableSummary();
  if (!enableSummary) { return null; }
  return (
    <StyledBox>
      <Typography variant="subtitle1">
        {t('details.summary.title')}
      </Typography>
      <Box>
        <SummaryContent
          session={session}
          generatedSummary={text}
          loading={loading}
          generateSummary={generateSummary}
        />
        {(session.summary || text) && (
          <CopyToClipboard
            text={session.summary || text}
            onCopy={() => {
              enqueueSnackbar(
                t('details.summary.copy-feedback'),
                {
                  style: { width: 300 },
                  autoHideDuration: 6000,
                  variant: 'success',
                  anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
                },
              );
            }}
          >
            <Button
              variant="contained"
              color="primary"
              size="small"
            >
              <ContentCopyIcon />
              {t('details.summary.copy')}
            </Button>
          </CopyToClipboard>
        )}
      </Box>
      {error && (<Alert severity="error">{error}</Alert>)}
    </StyledBox>
  );
}
